import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react';
import type { UrenEntryView, UrenKeuzelijst, UrenProjectTaken } from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '../api/client.js';

/**
 * Uren schrijven naar TimeChimp (Onderdeel 5). Weekweergave naar het model
 * van TimeChimp zelf: regels toevoegen, corrigeren zolang niet aangeboden,
 * en per week aanbieden. TimeChimp blijft de bron van waarheid.
 */

interface UrenResponse {
  entries: UrenEntryView[];
  weekHours: number | null;
}

const DAG_LABELS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

function toYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Maandag van de week waarin `d` valt (NL-week). */
function mondayOf(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(12, 0, 0, 0);
  const day = (copy.getDay() + 6) % 7;
  copy.setDate(copy.getDate() - day);
  return copy;
}

function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

const STATUS_LABEL: Record<string, string> = {
  Open: 'Geregistreerd',
  PendingApproval: 'Aangeboden',
  Approved: 'Goedgekeurd',
  Rejected: 'Afgekeurd',
  Invoiced: 'Gefactureerd',
  WrittenOff: 'Afgeboekt',
};

function StatusChip({ status }: { status: string }) {
  const label = STATUS_LABEL[status] ?? status;
  const cls =
    status === 'Open'
      ? 'bg-brand-50 text-brand-700'
      : status === 'Rejected'
        ? 'bg-red-50 text-red-700'
        : 'bg-ink-800/10 text-ink-900/70';
  return <span className={`rounded-sm px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

// ponytail: statisch voorbeeld voor niet-gekoppelde gebruikers; geen interactie nodig
const DEMO_ENTRIES = [
  {
    dag: 'ma 6 jul',
    project: 'Huisartsenpraktijk De Linde · POH GGZ',
    taak: 'Consulten',
    notes: 'consulten ochtend',
    uren: '8',
    status: 'Open',
  },
  {
    dag: 'di 7 jul',
    project: 'Huisartsenpraktijk De Linde · POH GGZ',
    taak: 'Consulten',
    notes: '',
    uren: '8',
    status: 'Open',
  },
  {
    dag: 'wo 8 jul',
    project: 'SUPPOHRT · Intervisie',
    taak: 'Overleg',
    notes: 'intervisiegroep',
    uren: '2',
    status: 'PendingApproval',
  },
];

const inputClass =
  'w-full rounded-md border border-ring2 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-900/40 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 ' +
  'disabled:cursor-not-allowed disabled:bg-ink-50 disabled:text-ink-900/50';

const labelClass = 'flex flex-col gap-1.5 text-xs font-semibold text-ink-900/70';

interface FormState {
  editingId: number | null;
  date: string;
  projectId: string;
  taskId: string;
  hours: string;
  notes: string;
  /** Per-week-invoer: uren ma t/m zo; leeg = die dag overslaan. */
  weekUren: string[];
}

export function UrenPage() {
  const [monday, setMonday] = useState(() => mondayOf(new Date()));
  const [entries, setEntries] = useState<UrenEntryView[]>([]);
  const [weekHours, setWeekHours] = useState<number | null>(null);
  const [keuzelijst, setKeuzelijst] = useState<UrenKeuzelijst | null>(null);
  const [taken, setTaken] = useState<UrenProjectTaken | null>(null);
  const [takenLoading, setTakenLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [notLinked, setNotLinked] = useState(false);
  const [notAvailable, setNotAvailable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [perWeek, setPerWeek] = useState(false);

  const range = useMemo(() => ({ from: toYmd(monday), to: toYmd(addDays(monday, 6)) }), [monday]);

  const emptyForm = useCallback(
    (): FormState => ({
      editingId: null,
      date:
        toYmd(new Date()) >= range.from && toYmd(new Date()) <= range.to
          ? toYmd(new Date())
          : range.from,
      projectId: '',
      taskId: '',
      hours: '',
      notes: '',
      weekUren: Array<string>(7).fill(''),
    }),
    [range],
  );
  const [form, setForm] = useState<FormState>(emptyForm);

  const loadWeek = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const resp = await apiGet<UrenResponse>('/api/v1/uren', range);
      setEntries(resp.entries);
      setWeekHours(resp.weekHours);
      setNotLinked(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'geen_timechimp_koppeling') {
        setNotLinked(true);
      } else if (err instanceof ApiError && err.status === 404) {
        setNotAvailable(true);
      } else {
        setErrorMsg(err instanceof ApiError ? err.message : 'Uren konden niet geladen worden.');
      }
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void loadWeek();
  }, [loadWeek]);

  useEffect(() => {
    (async () => {
      try {
        setKeuzelijst(await apiGet<UrenKeuzelijst>('/api/v1/uren/keuzelijsten'));
      } catch {
        // geen-koppeling / flag-uit wordt al door loadWeek afgehandeld
      }
    })();
  }, []);

  useEffect(() => {
    setForm(emptyForm());
  }, [emptyForm]);

  const selectedProject = keuzelijst?.projects.find((p) => String(p.id) === form.projectId) ?? null;

  // Activiteiten + toewijzing komen per gekozen project uit TimeChimp
  // (detailcall, backend cachet); zie het shared schema voor het waarom.
  useEffect(() => {
    if (!form.projectId) {
      setTaken(null);
      return;
    }
    let stale = false;
    setTakenLoading(true);
    (async () => {
      try {
        const resp = await apiGet<UrenProjectTaken>(
          `/api/v1/uren/keuzelijsten/${form.projectId}/taken`,
        );
        if (!stale) setTaken(resp);
      } catch {
        if (!stale) setTaken(null);
      } finally {
        if (!stale) setTakenLoading(false);
      }
    })();
    return () => {
      stale = true;
    };
  }, [form.projectId]);
  const openEntries = entries.filter((e) => e.editable);
  const weekTotal = entries.reduce((sum, e) => sum + e.hours, 0);
  /** Niets te kiezen: dan tonen we uitleg in plaats van een dood formulier. */
  const geenProjecten = Boolean(keuzelijst) && keuzelijst!.projects.length === 0;
  // Bewerken gaat altijd per regel; per week geldt alleen voor nieuwe invoer.
  const weekInvoer = perWeek && form.editingId === null;
  const nietGekoppeld = Boolean(selectedProject) && !takenLoading && !!taken && !taken.assigned;

  async function saveEntry(e: React.FormEvent) {
    e.preventDefault();
    if (!form.projectId || !form.taskId) return;
    if (weekInvoer) return saveWeek();
    if (!form.hours) return;
    setSaving(true);
    setErrorMsg(null);
    const body = {
      date: form.date,
      projectId: Number(form.projectId),
      taskId: Number(form.taskId),
      hours: Number(form.hours),
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    };
    try {
      if (form.editingId !== null) {
        await apiPut<UrenEntryView>(`/api/v1/uren/${form.editingId}`, body);
      } else {
        await apiPost<UrenEntryView>('/api/v1/uren', body);
      }
      setForm(emptyForm());
      setToast(form.editingId !== null ? 'Regel bijgewerkt.' : 'Regel toegevoegd.');
      await loadWeek();
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError
          ? err.message
          : 'Opslaan is niet gelukt. Je invoer staat nog in het formulier.',
      );
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  async function saveWeek() {
    const days = form.weekUren
      .map((h, i) => ({ date: toYmd(addDays(monday, i)), hours: Number(h) }))
      .filter((d) => d.hours > 0);
    if (days.length === 0) {
      setErrorMsg('Vul voor minstens één dag uren in.');
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    try {
      const resp = await apiPost<{ created: number }>('/api/v1/uren/week', {
        projectId: Number(form.projectId),
        taskId: Number(form.taskId),
        days,
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      });
      setForm(emptyForm());
      setToast(`${resp.created} ${resp.created === 1 ? 'regel' : 'regels'} toegevoegd.`);
      await loadWeek();
    } catch (err) {
      // Bij 'deels_opgeslagen' staan er al regels: die dagen leegmaken zodat
      // nog een klik niet dubbel boekt, herladen (dat wist errorMsg), en pas
      // daarna de melding. De rest van het formulier blijft staan.
      if (err instanceof ApiError && err.code === 'deels_opgeslagen') {
        const opgeslagen = (err.detail as { opgeslagen?: string[] } | undefined)?.opgeslagen ?? [];
        setForm((f) => ({
          ...f,
          weekUren: f.weekUren.map((h, i) =>
            opgeslagen.includes(toYmd(addDays(monday, i))) ? '' : h,
          ),
        }));
        await loadWeek();
      }
      setErrorMsg(
        err instanceof ApiError
          ? err.message
          : 'Opslaan is niet gelukt. Je invoer staat nog in het formulier.',
      );
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  async function kopieerVorigeWeek() {
    setSaving(true);
    setErrorMsg(null);
    try {
      const resp = await apiPost<{ created: number; overgeslagen: number }>(
        '/api/v1/uren/dupliceren',
        range,
      );
      setToast(
        `${resp.created} ${resp.created === 1 ? 'regel' : 'regels'} overgenomen uit de vorige week.` +
          (resp.overgeslagen > 0
            ? ` ${resp.overgeslagen} ${resp.overgeslagen === 1 ? 'regel stond' : 'regels stonden'} op een project dat niet (meer) aan jou is toegewezen en ${resp.overgeslagen === 1 ? 'is' : 'zijn'} overgeslagen.`
            : ''),
      );
      await loadWeek();
    } catch (err) {
      // Eerst herladen (bij 'deels_opgeslagen' staan er al regels), daarna de
      // melding zetten: loadWeek wist errorMsg zelf.
      await loadWeek();
      setErrorMsg(err instanceof ApiError ? err.message : 'Kopiëren is niet gelukt.');
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 4000);
    }
  }

  function startEdit(entry: UrenEntryView) {
    setForm({
      editingId: entry.id,
      date: entry.date,
      projectId: entry.projectId ? String(entry.projectId) : '',
      taskId: entry.taskId ? String(entry.taskId) : '',
      hours: String(entry.hours),
      notes: entry.notes ?? '',
      weekUren: Array<string>(7).fill(''),
    });
  }

  async function deleteEntry(id: number) {
    setSaving(true);
    setErrorMsg(null);
    try {
      await apiDelete(`/api/v1/uren/${id}`);
      if (form.editingId === id) setForm(emptyForm());
      await loadWeek();
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.message : 'Verwijderen is niet gelukt.');
    } finally {
      setSaving(false);
    }
  }

  async function submitWeek() {
    setConfirmSubmit(false);
    setSaving(true);
    setErrorMsg(null);
    try {
      const resp = await apiPost<{ submitted: number }>('/api/v1/uren/aanbieden', range);
      setToast(
        resp.submitted === 0
          ? 'Er stonden geen nieuwe regels om aan te bieden.'
          : `${resp.submitted} ${resp.submitted === 1 ? 'regel' : 'regels'} aangeboden ter goedkeuring.`,
      );
      await loadWeek();
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.message : 'Aanbieden is niet gelukt.');
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 4000);
    }
  }

  const isHuidigeWeek = toYmd(monday) === toYmd(mondayOf(new Date()));
  const weekTitle = `${monday.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })} t/m ${addDays(monday, 6).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}`;

  if (notAvailable) {
    return (
      <Layout>
        <div className="mx-auto w-full max-w-3xl px-4 py-6">
          <div className="rounded-2xl border border-ring2 bg-white px-6 py-10 text-center text-sm text-ink-900/65">
            Uren schrijven is nog niet beschikbaar. Neem contact op met kantoor.
          </div>
        </div>
      </Layout>
    );
  }

  if (notLinked) {
    return (
      <Layout>
        <div className="mx-auto w-full max-w-3xl px-4 py-6">
          <h1 className="mb-4 flex items-center gap-2 text-xl font-bold text-ink-900">
            <ClockIcon size={20} className="text-brand-700" /> Uren
          </h1>
          <div className="rounded-2xl border border-ring2 bg-white px-6 py-10 text-center">
            <AlertCircleIcon size={28} className="mx-auto mb-3 text-ink-900/40" />
            <p className="text-sm font-medium text-ink-900">
              Je account is nog niet gekoppeld aan TimeChimp.
            </p>
            <p className="mt-1 text-sm text-ink-900/65">
              Neem contact op met kantoor om je e-mailadres in TimeChimp te laten controleren.
              Daarna kun je hier je uren schrijven.
            </p>
          </div>

          {/* Statisch voorbeeld van het urenscherm na koppeling */}
          <div className="mt-6" aria-hidden="true">
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded-sm bg-ink-800/10 px-2 py-0.5 text-xs font-medium text-ink-900/70">
                Voorbeeld
              </span>
              <p className="text-sm text-ink-900/65">
                Zo ziet het urenscherm eruit zodra je account gekoppeld is:
              </p>
            </div>
            <div className="pointer-events-none select-none opacity-60">
              <div className="mb-3 flex items-center justify-between rounded-2xl border border-ring2 bg-white px-4 py-3 shadow-card">
                <span className="rounded-lg border border-ring2 bg-white p-1.5 text-ink-900">
                  <ChevronLeftIcon size={16} />
                </span>
                <div className="text-center">
                  <p className="text-sm font-semibold text-ink-900">6 juli t/m 12 juli 2026</p>
                  <p className="text-xs text-ink-900/55 tabular-nums">
                    18 uur geschreven van 24 contracturen
                  </p>
                </div>
                <span className="rounded-lg border border-ring2 bg-white p-1.5 text-ink-900">
                  <ChevronRightIcon size={16} />
                </span>
              </div>
              <div className="overflow-x-auto rounded-2xl border border-ring2 bg-white shadow-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ring2 text-left text-[12px] uppercase tracking-wide text-ink-900/55">
                      <th className="px-4 py-2 font-medium">Dag</th>
                      <th className="px-4 py-2 font-medium">Klant / project</th>
                      <th className="px-4 py-2 font-medium">Activiteit</th>
                      <th className="px-4 py-2 font-medium">Omschrijving</th>
                      <th className="px-4 py-2 text-right font-medium">Uren</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEMO_ENTRIES.map((e) => (
                      <tr key={e.dag} className="border-b border-ring2 last:border-b-0">
                        <td className="whitespace-nowrap px-4 py-2 text-ink-900">{e.dag}</td>
                        <td className="px-4 py-2 text-ink-900">{e.project}</td>
                        <td className="px-4 py-2 text-ink-900/70">{e.taak}</td>
                        <td className="px-4 py-2 text-ink-900/70">{e.notes}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink-900">{e.uren}</td>
                        <td className="px-4 py-2">
                          <StatusChip status={e.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex justify-end">
                <span className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white">
                  Uren deze week aanbieden
                </span>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto w-full max-w-5xl px-4 py-6">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
              <ClockIcon size={20} className="text-brand-700" /> Uren
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-ink-900/65">
              Schrijf je uren en bied ze per week aan. Ze staan meteen in TimeChimp.
            </p>
          </div>
        </div>

        {errorMsg && (
          <p className="mb-4 inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircleIcon size={14} /> {errorMsg}
          </p>
        )}
        {toast && (
          <p className="mb-4 inline-flex items-center gap-1.5 rounded-md bg-brand-50 px-3 py-2 text-sm text-brand-700">
            <CheckCircle2Icon size={14} /> {toast}
          </p>
        )}

        {/* Weeknavigatie */}
        <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-ring2 bg-white px-3 py-2.5 shadow-card">
          <button
            type="button"
            onClick={() => setMonday((m) => addDays(m, -7))}
            className="rounded-lg border border-ring2 bg-white p-2 text-ink-900 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:opacity-50"
            aria-label="Vorige week"
            disabled={saving}
          >
            <ChevronLeftIcon size={16} />
          </button>
          <div className="min-w-0 text-center">
            <p className="truncate text-sm font-bold text-ink-900">{weekTitle}</p>
            <p className="text-xs tabular-nums text-ink-900/55">
              <span className="font-semibold text-ink-900/80">
                {weekTotal.toLocaleString('nl-NL')} uur
              </span>{' '}
              geschreven{weekHours !== null ? ` van ${weekHours} contracturen` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!isHuidigeWeek && (
              <button
                type="button"
                disabled={saving}
                onClick={() => setMonday(mondayOf(new Date()))}
                className="hidden rounded-lg border border-ring2 bg-white px-3 py-1.5 text-xs font-semibold text-ink-900 hover:bg-brand-50 sm:block"
              >
                Deze week
              </button>
            )}
            <button
              type="button"
              onClick={() => setMonday((m) => addDays(m, 7))}
              className="rounded-lg border border-ring2 bg-white p-2 text-ink-900 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:opacity-50"
              aria-label="Volgende week"
              disabled={saving}
            >
              <ChevronRightIcon size={16} />
            </button>
          </div>
        </div>

        {/* Invoerformulier — vervalt volledig als er niets te kiezen valt:
            een formulier dat niets kan doen is verwarrender dan een uitleg. */}
        {geenProjecten ? (
          <div className="mb-4 rounded-2xl border border-ring2 bg-white p-6 shadow-card">
            <div className="flex flex-col items-center gap-3 text-center">
              <span className="rounded-full bg-mosterdgeel-10 p-3 text-mosterdgeel-600">
                <AlertCircleIcon size={24} />
              </span>
              <h2 className="text-base font-bold text-ink-900">Je kunt nog geen uren schrijven</h2>
              <p className="max-w-md text-sm text-ink-900/65">
                Er is in TimeChimp nog geen praktijk aan jou gekoppeld. Kantoor koppelt je aan het
                juiste project; daarna verschijnt het hier vanzelf.
              </p>
            </div>
          </div>
        ) : (
          <form
            onSubmit={saveEntry}
            className="mb-4 rounded-2xl border border-ring2 bg-white p-4 shadow-card md:p-5"
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-ink-900">
                {form.editingId !== null ? 'Regel bijwerken' : 'Uren toevoegen'}
              </h2>
              {form.editingId !== null ? (
                <button
                  type="button"
                  onClick={() => setForm(emptyForm())}
                  className="text-xs font-semibold text-petrol-500 hover:underline"
                >
                  Annuleren
                </button>
              ) : (
                <div className="inline-flex rounded-lg border border-ring2 p-0.5 text-xs font-semibold">
                  {[
                    { label: 'Per dag', value: false },
                    { label: 'Per week', value: true },
                  ].map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      aria-pressed={perWeek === o.value}
                      onClick={() => setPerWeek(o.value)}
                      className={`rounded-md px-3 py-1 ${perWeek === o.value ? 'bg-brand-500 text-white' : 'text-ink-900/70 hover:bg-brand-50'}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-12">
              {!weekInvoer && (
                <label className={`${labelClass} col-span-2 md:col-span-3`}>
                  Datum
                  <input
                    type="date"
                    required
                    min={range.from}
                    max={range.to}
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    className={inputClass}
                  />
                </label>
              )}
              <label
                className={`${labelClass} col-span-2 ${weekInvoer ? 'md:col-span-7' : 'md:col-span-5'}`}
              >
                Klant en project
                <select
                  required
                  value={form.projectId}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, projectId: e.target.value, taskId: '' }))
                  }
                  className={inputClass}
                >
                  <option value="">Kies een project</option>
                  {(keuzelijst?.projects.some((p) => p.recent) ?? false) && (
                    <optgroup label="Recent gebruikt">
                      {keuzelijst?.projects
                        .filter((p) => p.recent)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.customerName ? `${p.customerName} · ${p.name}` : p.name}
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {/* Niet "Alle projecten": de lijst toont alleen wat aan jou is
                      toegewezen in TimeChimp. */}
                  {keuzelijst?.projects.some((p) => !p.recent) && (
                    <optgroup label="Je overige projecten">
                      {keuzelijst.projects
                        .filter((p) => !p.recent)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.customerName ? `${p.customerName} · ${p.name}` : p.name}
                          </option>
                        ))}
                    </optgroup>
                  )}
                </select>
              </label>
              <label
                className={`${labelClass} col-span-2 ${weekInvoer ? 'md:col-span-5' : 'md:col-span-4'}`}
              >
                Activiteit
                <select
                  required
                  value={form.taskId}
                  disabled={!selectedProject || takenLoading || !taken?.assigned}
                  onChange={(e) => setForm((f) => ({ ...f, taskId: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">
                    {!selectedProject ? 'Kies eerst een project' : takenLoading ? 'Laden…' : 'Kies'}
                  </option>
                  {taken?.assigned &&
                    taken.tasks.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </label>

              {nietGekoppeld && (
                <p className="col-span-2 flex items-start gap-2 rounded-md bg-mosterdgeel-10 px-3 py-2 text-xs text-mosterdgeel-600 md:col-span-12">
                  <AlertCircleIcon size={14} className="mt-px shrink-0" />
                  Je bent in TimeChimp niet aan dit project gekoppeld. Kies een ander project of
                  neem contact op met kantoor.
                </p>
              )}
              {keuzelijst?.fallback && (
                <p className="col-span-2 flex items-start gap-2 rounded-md bg-ink-50 px-3 py-2 text-xs text-ink-900/65 md:col-span-12">
                  <AlertCircleIcon size={14} className="mt-px shrink-0" />
                  Je ziet nu alleen de projecten waarop je eerder uren schreef. Mis je een project?
                  Neem contact op met kantoor.
                </p>
              )}

              <label className={`${labelClass} col-span-2 md:col-span-7`}>
                Omschrijving (optioneel)
                <input
                  type="text"
                  maxLength={500}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className={inputClass}
                  placeholder="Bijv. consulten ochtend"
                />
              </label>
              {weekInvoer ? (
                <div className="col-span-2 grid grid-cols-7 gap-1.5 md:col-span-12">
                  {DAG_LABELS.map((dag, i) => (
                    <label key={dag} className={labelClass}>
                      <span>
                        {dag}{' '}
                        <span className="font-normal text-ink-900/50">
                          {addDays(monday, i).getDate()}
                        </span>
                      </span>
                      <input
                        type="number"
                        min="0"
                        max="24"
                        step="0.25"
                        inputMode="decimal"
                        aria-label={`Uren ${dag}`}
                        value={form.weekUren[i]}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            weekUren: f.weekUren.map((h, j) => (j === i ? e.target.value : h)),
                          }))
                        }
                        className={`${inputClass} px-1.5 text-right tabular-nums`}
                      />
                    </label>
                  ))}
                </div>
              ) : (
                <label className={`${labelClass} col-span-1 md:col-span-2`}>
                  Uren
                  <input
                    type="number"
                    required
                    min="0.25"
                    max="24"
                    step="0.25"
                    value={form.hours}
                    onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
                    className={`${inputClass} text-right tabular-nums`}
                    placeholder="8"
                  />
                </label>
              )}
              <div
                className={`flex items-end md:col-span-3 ${weekInvoer ? 'col-span-2 md:col-start-10' : 'col-span-1'}`}
              >
                <button
                  type="submit"
                  disabled={saving || !form.taskId}
                  className="w-full rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:cursor-not-allowed disabled:bg-ink-100 disabled:text-ink-900/40"
                >
                  {form.editingId !== null
                    ? 'Bijwerken'
                    : weekInvoer
                      ? 'Week toevoegen'
                      : 'Toevoegen'}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Weektabel */}
        <div className="overflow-x-auto rounded-2xl border border-ring2 bg-white shadow-card">
          {loading ? (
            <div className="px-6 py-10 text-center text-sm text-ink-900/55">Laden…</div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
              <ClockIcon size={32} className="text-ink-900/20" />
              <p className="text-sm font-semibold text-ink-900/70">Nog geen uren in deze week</p>
              {!geenProjecten && !errorMsg && (
                <>
                  <p className="text-sm text-ink-900/55">
                    Voeg hierboven je eerste regel toe, of neem de uren van vorige week over.
                  </p>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void kopieerVorigeWeek()}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-ring2 bg-white px-4 py-2 text-sm font-semibold text-ink-900 hover:bg-brand-50 disabled:opacity-50"
                  >
                    <CopyIcon size={15} /> Vorige week kopiëren
                  </button>
                </>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ring2 text-left text-[12px] uppercase tracking-wide text-ink-900/55">
                  <th className="px-4 py-2 font-medium">Dag</th>
                  <th className="px-4 py-2 font-medium">Klant / project</th>
                  <th className="px-4 py-2 font-medium">Activiteit</th>
                  <th className="px-4 py-2 font-medium">Omschrijving</th>
                  <th className="px-4 py-2 text-right font-medium">Uren</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const d = new Date(`${e.date}T12:00:00`);
                  return (
                    <tr key={e.id} className="border-b border-ring2 last:border-b-0">
                      <td className="whitespace-nowrap px-4 py-2 text-ink-900">
                        {DAG_LABELS[(d.getDay() + 6) % 7]}{' '}
                        {d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                      </td>
                      <td className="px-4 py-2 text-ink-900">
                        {e.customerName ? `${e.customerName} · ` : ''}
                        {e.projectName ?? '–'}
                      </td>
                      <td className="px-4 py-2 text-ink-900/70">{e.taskName ?? '–'}</td>
                      <td className="max-w-[16rem] truncate px-4 py-2 text-ink-900/70">
                        {e.notes ?? ''}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-ink-900">
                        {e.hours.toLocaleString('nl-NL')}
                      </td>
                      <td className="px-4 py-2">
                        <StatusChip status={e.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        {e.editable && (
                          <>
                            <button
                              type="button"
                              onClick={() => startEdit(e)}
                              className="mr-1 rounded p-1 text-ink-900/55 hover:bg-brand-50 hover:text-brand-700"
                              aria-label="Regel bewerken"
                            >
                              <PencilIcon size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => void deleteEntry(e.id)}
                              className="rounded p-1 text-ink-900/55 hover:bg-red-50 hover:text-red-700"
                              aria-label="Regel verwijderen"
                            >
                              <Trash2Icon size={15} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-ring2 bg-ink-50/60">
                  <td
                    className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-ink-900/55"
                    colSpan={4}
                  >
                    Totaal deze week
                  </td>
                  <td className="px-4 py-2 text-right text-sm font-bold tabular-nums text-ink-900">
                    {weekTotal.toLocaleString('nl-NL')}
                  </td>
                  <td className="px-4 py-2" colSpan={2} />
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {!geenProjecten && (
          <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-ring2 bg-white px-4 py-3 shadow-card sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-ink-900/60">
              {openEntries.length > 0
                ? `${openEntries.length} ${openEntries.length === 1 ? 'regel staat' : 'regels staan'} klaar om aan te bieden. Daarna kun je ze alleen nog in TimeChimp laten aanpassen.`
                : 'Er staan geen nieuwe regels klaar om aan te bieden.'}
            </p>
            <button
              type="button"
              disabled={saving || openEntries.length === 0}
              onClick={() => setConfirmSubmit(true)}
              className="shrink-0 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:cursor-not-allowed disabled:bg-ink-100 disabled:text-ink-900/40"
            >
              Uren deze week aanbieden
            </button>
          </div>
        )}

        {confirmSubmit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div role="dialog" className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h3 className="text-base font-bold text-ink-900">Uren aanbieden</h3>
              <p className="mt-2 text-sm text-ink-900/70">
                Je biedt {openEntries.length} {openEntries.length === 1 ? 'regel' : 'regels'} (
                {openEntries.reduce((s, e) => s + e.hours, 0).toLocaleString('nl-NL')} uur) aan ter
                goedkeuring. Daarna kun je ze hier niet meer wijzigen.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmSubmit(false)}
                  className="rounded-lg border border-ring2 bg-white px-4 py-2 text-sm font-medium text-ink-900 hover:bg-brand-50"
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  onClick={() => void submitWeek()}
                  className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
                >
                  Aanbieden
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
